/** Public shell. The media editor loads only for an approved, authenticated account. */
import { auth } from "./auth-client";
import { renderAuth, renderAccess } from "./auth-screen";
import { node, message, errorText, feedback, action, link, field, hint, form, submit, installTheme, actionOrigin } from "./account-ui";
import { UNAVAILABLE, copy as fallbackCopy } from "../../server/copy";
import { parseCopy } from "./copy";
import { api, ApiError, checkedDraft, listDrafts, me } from "./api";
import { parseViewer, type DraftSummary, type Viewer } from "./core/workspace";
import { initialise, copy } from "./session";
import { flushDrafts, stopDrafts } from "./projects";
const root = document.getElementById("workspace");
if (!root) throw new Error("Application shell missing");
const workspace: HTMLElement = root;
const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel("subty-session");
channel?.addEventListener("message", (event: MessageEvent<unknown>) => {
  if (event.data === "logout") { stopDrafts(); location.replace("/"); }
});
function navigation(viewer: Viewer | null): void {
  const nav = document.getElementById("navigation"); if (!nav) return;
  nav.replaceChildren(); nav.setAttribute("aria-label", copy().account);
  const crumb = document.getElementById("crumb"); if (crumb) crumb.textContent = viewer?.email ?? "";
  if (viewer) {
    if (viewer.status === "approved") { nav.append(link(copy().projects, "/projects"), link(copy().account, "/account")); if (viewer.role === "admin") nav.append(link(copy().administration, "/admin")); }
    for (const anchor of nav.querySelectorAll("a")) if (anchor.pathname === location.pathname) anchor.setAttribute("aria-current", "page");
    nav.append(action(copy().logout, async () => {
      await flushDrafts(); stopDrafts(); await api("/api/auth/sign-out", "POST", {});
      channel?.postMessage("logout"); location.replace("/");
    }));
  }
}
function balance(viewer: Viewer): HTMLElement {
  const container = node("section", undefined, "balance-card");
  container.append(node("h2", copy().balance));
  const remaining = viewer.balance.remainingMs;
  container.append(node("div", remaining === null ? copy().unlimited : `${(remaining / 60000).toLocaleString("ru-RU", { maximumFractionDigits: 1 })} ${copy().minutes}`, "balance-value num"));
  container.append(node("p", `${copy().used}: ${(viewer.balance.usedMs / 60000).toFixed(1)} ${copy().minutes} · ${copy().held}: ${(viewer.balance.heldMs / 60000).toFixed(1)} ${copy().minutes}`, "hint"));
  container.append(node("p", copy().resetAt, "hint")); return container;
}
function modal(title: string, content: HTMLElement, saveLabel: string, save: () => Promise<void>): void {
  const opener = actionOrigin() ?? document.activeElement;
  const dialog = node("dialog", undefined, "account-dialog"); const heading = node("h2", title); heading.id = `dialog-${crypto.randomUUID()}`;
  dialog.setAttribute("aria-labelledby", heading.id);
  const error = node("p", "", "form-status"); error.setAttribute("role", "status");
  const confirm = action(saveLabel, async () => {
    for (const input of content.querySelectorAll("input")) if (!input.disabled && !input.reportValidity()) return;
    feedback(error, copy().processing);
    try { await save(); dialog.close(); } catch (why) { feedback(error, errorText(why), "error"); }
  }, true);
  if (saveLabel === copy().remove) confirm.className = "danger";
  const buttons = node("div", undefined, "row dialog-actions"); buttons.append(action(copy().cancel, () => dialog.close()), confirm);
  dialog.append(heading, content, error, buttons); document.body.append(dialog);
  dialog.addEventListener("close", () => {
    dialog.remove();
    if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    else { const heading = workspace.querySelector("h1"); if (heading) { heading.tabIndex = -1; heading.focus(); } }
  }, { once: true }); dialog.showModal();
}
async function dashboard(offset = 0): Promise<void> {
  workspace.className = "account-workspace"; workspace.replaceChildren();
  const header = node("div", undefined, "page-heading"); header.append(node("h1", copy().projects), link(copy().newProject, "/editor/new", "primary button-link")); workspace.append(header);
  const rows = await listDrafts(offset);
  if (!rows.length && offset === 0) {
    const empty = node("section", undefined, "empty-state"); empty.append(node("h2", copy().emptyProjects), node("p", copy().emptyHint, "hint")); workspace.append(empty);
  }
  for (const draft of rows) projectRow(draft);
  const controls = node("div", undefined, "row pagination");
  if (offset) controls.append(action(copy().previous, () => dashboard(Math.max(0, offset - 100))));
  if (rows.length === 100) controls.append(action(copy().next, () => dashboard(offset + 100)));
  workspace.append(controls);
}
function projectRow(draft: DraftSummary): void {
  const row = node("article", undefined, "project-row");
  const info = node("div", undefined, "project-info"); info.append(link(draft.title, `/editor/${draft.id}`, "project-title"), node("p", `${draft.name} · ${new Date(draft.updatedAt).toLocaleString("ru-RU")}`, "hint"));
  const buttons = node("div", undefined, "row");
  buttons.append(link(copy().open, `/editor/${draft.id}`), action(copy().duplicate, async () => {
    const result = checkedDraft(await api(`/api/drafts/${draft.id}/duplicate`, "POST", { id: crypto.randomUUID(), title: (draft.title + copy().copySuffix).slice(0, 255) })); location.assign(`/editor/${result.id}`);
  }), action(copy().rename, () => {
    const name = field(copy().projectName, "text", "off", draft.title); name.input.maxLength = 255;
    modal(copy().rename, name.label, copy().save, async () => {
      if (!name.input.reportValidity()) return;
      const fresh = checkedDraft(await api(`/api/drafts/${draft.id}`));
      await api(`/api/drafts/${draft.id}`, "PUT", { revision: fresh.revision, title: name.input.value.trim(), document: fresh.document }); await dashboard();
    });
  }), action(copy().remove, () => {
    modal(copy().remove, node("p", copy().confirmDelete), copy().remove, async () => { await api(`/api/drafts/${draft.id}`, "DELETE"); await dashboard(); });
  }));
  row.append(info, buttons); workspace.append(row);
}
async function accountScreen(viewer: Viewer): Promise<void> {
  workspace.className = "account-workspace";
  const header = node("div", undefined, "page-heading");
  const intro = node("div"); intro.append(node("h1", copy().account), node("p", copy().accountText, "form-intro")); header.append(intro);
  const layout = node("div", undefined, "account-layout");
  const allowance = balance(viewer); allowance.classList.add("account-allowance");
  const settings = node("div", undefined, "account-settings"); layout.append(allowance, settings); workspace.replaceChildren(header, layout);
  const password = form(copy().passwordChange, copy().sessionsRevoked, "h2");
  const current = field(copy().currentPassword, "password", "current-password"), next = field(copy().newPassword, "password", "new-password"); next.input.minLength = 12;
  password.form.append(current.label, next.label, hint(next.input, copy().passwordHint));
  submit(password.form, copy().save, password.status, async () => {
    await api("/api/auth/change-password", "POST", { currentPassword: current.input.value, newPassword: next.input.value, revokeOtherSessions: true });
    current.input.value = ""; next.input.value = ""; feedback(password.status, copy().passwordChanged, "success");
  }); settings.append(password.container);
  const keys = node("section", undefined, "passkeys"); keys.append(node("h2", copy().passkeys), node("p", copy().passkeyDescription, "form-intro"));
  const list = node("div", undefined, "passkey-list"); const status = node("p", "", "form-status"); status.setAttribute("role", "status");
  const clientFailure = (message: string | undefined): ApiError => new ApiError(400, { error: message && /[А-Яа-я]/.test(message) ? message : copy().requestFailed });
  const load = async (): Promise<void> => {
    feedback(status, copy().loading);
    try {
      const listed = await auth.passkey.listUserPasskeys();
      if (listed.error) throw clientFailure(listed.error.message);
      list.replaceChildren();
      if (!listed.data?.length) list.append(node("p", copy().noPasskeys, "hint"));
      for (const key of listed.data ?? []) {
        const row = node("div", undefined, "project-row"); row.append(node("span", key.name || copy().passkeyDefault), action(copy().remove, () => {
          const content = node("div"); content.append(node("strong", key.name || copy().passkeyDefault), node("p", copy().confirmPasskeyDelete, "form-intro"));
          modal(copy().remove, content, copy().remove, async () => {
            const result = await auth.passkey.deletePasskey({ id: key.id }); if (result.error) throw clientFailure(result.error.message); await load();
          });
        })); list.append(row);
      }
      feedback(status, "");
    } catch {
      list.replaceChildren(action(copy().retry, load)); feedback(status, copy().passkeyLoadFailed, "error");
    }
  };
  keys.append(list, status, action(copy().addPasskey, () => {
    const name = field(copy().passkeyName, "text", "off", copy().passkeyDefault); name.input.maxLength = 100;
    modal(copy().addPasskey, name.label, copy().addPasskey, async () => {
      const result = await auth.passkey.addPasskey({ name: name.input.value.trim() }); if (result.error) throw clientFailure(result.error.message);
      await load(); message(copy().passkeyAdded);
    });
  })); settings.append(keys); await load();
}
async function administration(viewer: Viewer, offset = 0): Promise<void> {
  if (viewer.role !== "admin") { location.replace("/projects"); return; }
  workspace.className = "account-workspace"; workspace.replaceChildren(node("h1", copy().administration));
  const data = await api(`/api/admin/users?offset=${offset}`);
  if (!Array.isArray(data)) throw new Error("Invalid user list");
  for (const value of data) {
    const user = parseViewer(value); if (!user) throw new Error("Invalid user response");
    const row = node("article", undefined, "user-row"); const info = node("div");
    info.append(node("strong", user.name), node("p", user.email), node("p", `${copy()[user.role]} · ${copy()[user.status]}${user.emailVerified ? "" : ` · ${copy().unverified}`}`, "hint"));
    const buttons = node("div", undefined, "row");
    const change = async (changes: unknown): Promise<void> => { await api(`/api/admin/users/${user.id}`, "PATCH", changes); await administration(viewer, offset); };
    if (user.status !== "approved" && user.emailVerified) buttons.append(action(copy().approve, () => change({ status: "approved" })));
    if (user.status === "pending") buttons.append(action(copy().reject, () => change({ status: "rejected" })));
    if (user.status === "approved") buttons.append(action(copy().suspend, () => change({ status: "suspended" })));
    buttons.append(action(copy().settings, () => {
      const container = node("div", undefined, "account-form");
      const monthly = field(copy().monthlyLabel, "text", "off", String((user.balance.monthlyAllowanceMs ?? 3600000) / 60000)); monthly.input.inputMode = "decimal";
      const remaining = field(copy().remainingLabel, "text", "off", String((user.balance.remainingMs ?? 0) / 60000)); remaining.input.inputMode = "decimal";
      const unlimited = node("label", undefined, "check"); const checkbox = node("input"); checkbox.type = "checkbox"; checkbox.checked = user.balance.monthlyAllowanceMs === null;
      unlimited.append(checkbox, node("span", copy().unlimitedLabel));
      const role = node("div", undefined, "row"); let selected = user.role;
      const normal = action(copy().user, () => { selected = "user"; paintRole(); }); const admin = action(copy().admin, () => { selected = "admin"; paintRole(); });
      const paintRole = (): void => { normal.setAttribute("aria-pressed", String(selected === "user")); admin.setAttribute("aria-pressed", String(selected === "admin")); };
      paintRole(); role.append(normal, admin); container.append(unlimited, monthly.label, remaining.label, node("span", copy().role, "field"), role);
      const toggle = (): void => { monthly.input.disabled = checkbox.checked; remaining.input.disabled = checkbox.checked; }; checkbox.onchange = toggle; toggle();
      modal(copy().settings, container, copy().save, async () => {
        const number = (value: string): number => { const parsed = Number(value.replace(",", ".")); if (!value.trim() || !Number.isFinite(parsed) || parsed < 0) throw new ApiError(400, { error: copy().requestFailed }); return Math.round(parsed * 60000); };
        await change({ role: selected, monthlyMs: checkbox.checked ? null : number(monthly.input.value), ...(checkbox.checked ? {} : { remainingMs: number(remaining.input.value) }) });
      });
    })); row.append(info, buttons); workspace.append(row);
  }
  const controls = node("div", undefined, "row pagination"); if (offset) controls.append(action(copy().previous, () => administration(viewer, offset - 100))); if (data.length === 100) controls.append(action(copy().next, () => administration(viewer, offset + 100))); workspace.append(controls);
}
async function editor(id: string | null): Promise<void> {
  const template = document.getElementById("editor-template"); if (!(template instanceof HTMLTemplateElement)) throw new Error("Editor template missing");
  workspace.replaceWith(template.content.cloneNode(true));
  for (const [id, key] of [["draft-title-label", "projectName"], ["conflict-copy", "keepCopy"], ["conflict-server", "useServer"], ["asr-managed", "managed"], ["asr-custom", "custom"], ["managed-hint", "managedHint"]] as const) { const element = document.getElementById(id); if (element) element.textContent = copy()[key]; }
  const main = await import("./main"); await main.startEditor(id);
}
async function boot(): Promise<void> {
  const language = parseCopy(await api("/api/copy"));
  if (!language) throw new Error("Invalid product copy");
  initialise(null, language);
  installTheme();
  const brand = document.querySelector(".wordmark"); brand?.setAttribute("aria-label", copy().home);
  let viewer: Viewer | null = null;
  try { viewer = await me(); } catch (error) { if (!(error instanceof ApiError) || error.status !== 401) throw error; }
  initialise(viewer, language); navigation(viewer);
  if (location.pathname === "/reset-password") { renderAuth(workspace); return; }
  if (!viewer) { renderAuth(workspace); return; }
  if (viewer.status !== "approved") { renderAccess(workspace, viewer); return; }
  if (location.pathname === "/account") await accountScreen(viewer);
  else if (location.pathname === "/admin") await administration(viewer);
  else if (location.pathname.startsWith("/editor/")) await editor(location.pathname.slice(8) === "new" ? null : location.pathname.slice(8));
  else await dashboard();
}
void boot().catch((error: unknown) => {
  const current = document.querySelector("main") ?? workspace;
  initialise(null, fallbackCopy);
  const card = form(UNAVAILABLE, error instanceof ApiError ? error.message : undefined); card.form.remove(); card.status.remove();
  card.container.append(action(fallbackCopy.retry, () => location.reload(), true));
  // Even bootstrap/network failures retain a usable, branded retry screen.
  const intro = node("div", undefined, "startup-error"); intro.append(card.container); current.className = "startup-workspace"; current.replaceChildren(intro);
});
